<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { reactive, ref, defineProps, defineEmits, watch } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import MarketProduitFormAddVariantModal from '../modals/MarketProduitFormAddVariantModal.vue';
    import { AMOUNT_FORMAT_CONFIG, INTEGER_FORMAT_CONFIG } from '@/utils';
    import MarketProduitFormEditVariantModal from '../modals/MarketProduitFormEditVariantModal.vue';
    import MarketAttributsListModal from '../../market-attributs/modals/MarketAttributsListModal.vue';

    const props = defineProps({
        modelValue: { type: Object, default: null },
        startupFormDatas: { type: Object, required: true },
        imagePreview: { type: String, required: false },
        id: { type: Number, default: null },
    });
    const emit = defineEmits(['update:modelValue']);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const addVariantModalRef = ref<InstanceType<typeof MarketProduitFormAddVariantModal> | null>(null);
    const editVariantModalRef = ref<InstanceType<typeof MarketProduitFormEditVariantModal> | null>(null);
    const attributsListModalRef = ref<InstanceType<typeof MarketAttributsListModal> | null>(null);
    const loading = ref(false);
    const formData = reactive({
        attributs: [] as any[],
        produit_attributs: [] as any[],
    });

    watch(props, () => {
        if (props.modelValue) {
            Object.assign(formData, props.modelValue);
            formData.produit_attributs.forEach((pAttribut) => {
                if (pAttribut.image && !pAttribut.image_preview) {
                    pAttribut.image_preview = pAttribut.image_url;
                }
            });
        }
    }, { deep: true });

    watch(formData, () => {
        emit('update:modelValue', formData);
    }, { deep: true });

    function addAttribut() {
        attributsListModalRef.value?.open().then((result) => {
            if (result) {
                const alreadyExists = formData.attributs.filter(attribut => attribut.nom === result.nom).length > 0;
                if (!alreadyExists) {
                    formData.attributs.push(result);
                }
            }
        });
    }

    async function onRemoveAttribut(attributIndex: number) {
        const confirmation = await confirmationModalRef.value?.open("Suppression Attribut", "Voulez-vous vraiment supprimer l'attribut sélectionné ?");
        if (confirmation) {
            if (formData.produit_attributs.filter(pAttribut => Object.keys(pAttribut.valeurs).lastIndexOf(formData.attributs[attributIndex].nom) > -1).length > 0) {
                throw new Error("Impossible de supprimer cet attribut car il est utilisé par une ou plusieurs variantes.");
            }
            //formData.variants = formData.variants.filter(variant => Object.keys(variant.valeurs).lastIndexOf(formData.attributs[attributIndex].nom) === -1);
            formData.attributs.splice(attributIndex, 1);
        }
    }

    async function openAddVariantModal() {
        const result = await addVariantModalRef.value?.open(props.startupFormDatas.produit);
        if (result) {
            formData.produit_attributs.push({
                guid: result.guid,
                nom: result.nom,
                valeurs: result.valeurs,
                produit_attributs: result.produit_attributs,
                montant: props.startupFormDatas.produit.montant,
                quantite: 0,
                images: [] as any[],
                images_previews: [] as any[],
            });
        }
    }

    async function openEditVariantModal(variant: any, index: number) {
        const result = await editVariantModalRef.value?.open(props.startupFormDatas.produit, variant);
        if (result) {
            Object.assign(formData.produit_attributs[index], {
                montant: result.montant,
                quantite: result.quantite,
                images: result.images ? result.images : [],
                images_previews: result.images_previews ? result.images_previews : [],
            });
        }
    }

    function onFileImageUploaded(variant: any, index: number, uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.produit_attributs[index].image_preview = uploadedFile.file_preview;
        formData.produit_attributs[index].image = { file: uploadedFile.file, name: uploadedFile.file_name };
    }
</script>

<template>
    <div>
        <h5 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
            Gestion des attributs
        </h5>
        <table class="table">
            <thead>
                <tr>
                    <th width="45%">Attributs</th>
                    <th width="45%">Valeurs</th>
                    <th width="10%"></th>
                </tr>
            </thead>
            <tbody>
                <template v-if="formData.attributs.length == 0">
                    <tr>
                        <td colspan="3">
                            <div class="text-center text-grey-700">Aucun attribut ajouté</div>
                        </td>
                    </tr>
                </template>
                <tr v-for="(attribut, index) in formData.attributs" :key="'attribut_row_' + index">
                    <td>
                        <h6>{{ attribut.nom }}</h6>
                    </td>
                    <td>
                        <h6>{{ attribut.valeurs.join(', ') }}</h6>
                    </td>
                    <td>
                        <custom-button @click="onRemoveAttribut(index)" :classesList="'btn-ultra white2 tips'">
                            <img src="/icons/icon-delete.svg" class="btn-icon" alt="" />
                            <span>Supprimer</span>
                        </custom-button>
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td colspan="3" class="text-center">
                        <custom-button @click="addAttribut" type="button" :classesList="'btn lg w-100'" :loading="loading">
                            <span>Ajouter un attribut</span>
                        </custom-button>
                    </td>
                </tr>
            </tfoot>
        </table>

        <div class="clear-both h-30"></div>
        <hr class="s20 my-0 hr2 opacity-1"/>
        <div class="clear-both"></div>

        <h5 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
            Gestion des variantes
        </h5>
        <table class="table">
            <thead>
                <tr>
                    <th></th>
                    <th>Variante</th>
                    <th>Prix unitaire</th>
                    <th>Stock initial</th>
                    <th>Images</th>
                    <th width="10%"></th>
                </tr>
            </thead>
            <tbody>
                <template v-if="formData.produit_attributs.length == 0">
                    <tr>
                        <td colspan="6">
                            <div class="text-center text-grey-700">Aucune variante ajoutée</div>
                        </td>
                    </tr>
                </template>
                <tr v-for="(pAttribut, index) of formData.produit_attributs" :key="'produit_attribut_row_' + index">
                    <td>
                        <div class="flex">
                            <custom-upload-input :id="'image_input_' + index" label="Image" type="IMAGE" size="small" :preview="pAttribut.image_preview ?? imagePreview" @uploaded="onFileImageUploaded(pAttribut, index, $event)"></custom-upload-input>
                        </div>
                    </td>
                    <td>
                        <div v-for="(attribut, attrIndex) of formData.attributs" :key="'produit_attribut_' + index + '_attribut_' + attrIndex">
                            <strong>{{ attribut.nom }}:</strong> {{ pAttribut.valeurs[attribut.nom] }}<br/>
                        </div>
                    </td>
                    <td>
                        <div class="form-field-floating reactive active my-4">
                            <money3 class="form-input" v-model="pAttribut.montant" name="montant" id="montant" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG"></money3>
                            <label for="montant">Prix unitaire <b class="color-danger">*</b></label>
                        </div>
                    </td>
                    <td>
                        <div class="form-field-floating reactive active my-4">
                            <money3 class="form-input" v-model="pAttribut.quantite" name="quantite" id="quantite" placeholder="" v-bind="INTEGER_FORMAT_CONFIG"></money3>
                            <label for="quantite">Stock initial</label>
                        </div>
                    </td>
                    <td>
                        {{ pAttribut.images.length }} image(s)
                    </td>
                    <td>
                        <div class="flex-right">
                            <custom-button :classesList="'btn-ultra white2 tips'" @click="openEditVariantModal(pAttribut, index)">
                                <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                <span>Modifier</span>
                            </custom-button>
                            <custom-button :classesList="'btn-ultra white2 tips'">
                                <img src="/icons/icon-delete.svg" class="btn-icon" alt="" />
                                <span>Supprimer</span>
                            </custom-button>
                        </div>
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td colspan="6" class="text-center">
                        <custom-button type="button" :classesList="'btn lg w-100'" :loading="loading" @click="openAddVariantModal">
                            <span>Ajouter un variant</span>
                        </custom-button>
                    </td>
                </tr>
            </tfoot>
        </table>

        <div class="clear-both h-30"></div>
        <hr class="s20 my-0 hr2 opacity-1"/>
        <div class="clear-both"></div>

        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
        <market-produit-form-add-variant-modal ref="addVariantModalRef" :attributs="formData.attributs"></market-produit-form-add-variant-modal>
        <market-produit-form-edit-variant-modal ref="editVariantModalRef"></market-produit-form-edit-variant-modal>
        <market-attributs-list-modal ref="attributsListModalRef" />
    </div>
</template>
